import {Evaluable, evaluateWhenFunction} from "../evaluable";
import {
    AssertionLogicFunction,
    assertType,
    NarrowedNonUnionTypeAssertController
} from "../type-assertion/type-assertion";

/**
 * The "original" branch of an `OverridableValue<V>`.
 *
 * `value` may be `undefined` to express "this property is in original mode but
 * the underlying original value is not yet known" — e.g. when an AI-generated
 * spec creates an original state without a concrete value to put inside.
 */
export type OverridableValueOriginalState<V> = {
    __original: true;
    value: V | undefined;
};

/** A value that can either be overridden with a custom value or use the original source value. */
export type OverridableValue<V> = V | OverridableValueOriginalState<V>;

export namespace OverridableValue {
    export type ResolveValueType<T> =
        T extends OverridableValueOriginalState<any> ? NonNullable<T['value']> :
        T extends OverridableValue<infer U> ? U :
        T;

    export type ResolveOverridableValuesKeys<T> = {
        [K in keyof T]-?: OverridableValueOriginalState<any> extends T[K] ? K : never
    }[keyof T]

    export type ResolveNonOverridableValuesKeys<T> = {
        [K in keyof T]-?: OverridableValueOriginalState<any> extends T[K] ? never : K
    }[keyof T]
}

export function overridableValueIsOriginal<V>(value: any): value is OverridableValueOriginalState<V> {
    return typeof value === 'object' && value !== null && (value as OverridableValueOriginalState<V>).__original === true;
}

export function overridableValueIsOverridden<V>(value: OverridableValue<V>): value is V {
    return !overridableValueIsOriginal(value);
}

export function overridableValueConvert<V, R>(
    overridableValue: OverridableValue<V>,
    convertFunction: (isOverridden: boolean, value: V | undefined) => R
): R {
    return convertFunction(overridableValueIsOverridden(overridableValue), overridableValueGetValue(overridableValue));
}

/**
 * Unwraps an `OverridableValue<V>` to its inner value.
 *
 * Without `defaultValue` the result may be `undefined` (when the input is an original
 * state with `value: undefined`). When `defaultValue` is supplied, it replaces an
 * `undefined` inner value and the return type is narrowed to `V`.
 *
 * Note: if `V` itself includes `undefined`, an *overridden* value of `undefined` will
 * also be replaced by `defaultValue` — the same behavior as `result ?? defaultValue`.
 */
export function overridableValueGetValue<V>(value: OverridableValue<V>): V | undefined;
export function overridableValueGetValue<V>(value: OverridableValue<V>, defaultValue: V): V;
export function overridableValueGetValue<V>(value: OverridableValue<V>, defaultValue?: V): V | undefined {
    const inner = overridableValueIsOriginal<V>(value) ? value.value : value;
    return inner !== undefined ? inner : defaultValue;
}

export function overridableValueModifyValue<V>(
    overridableValue: OverridableValue<V>,
    modificationFunction: Evaluable<(value: V | undefined) => V>
): OverridableValue<V> {
    const value = overridableValueGetValue(overridableValue);

    const modifiedValue = evaluateWhenFunction(modificationFunction, value);

    if (overridableValueIsOriginal(overridableValue)) {
        return overridableValueCreateOriginal(modifiedValue);
    } else {
        return modifiedValue;
    }
}

export function overridableValueGetOverriddenValue<V, O>(value: OverridableValue<V>, originalValue: O): V | O {
    if (overridableValueIsOriginal<V>(value)) {
        return originalValue;
    } else {
        return value;
    }
}

export function overridableValueCreateOriginal<V>(value: V | undefined): OverridableValueOriginalState<V> {
    return {
        __original: true,
        value: value
    }
}

export function overridableValueCreate<V>(value: V, isOriginal: boolean) {
    if (isOriginal) {
        return overridableValueCreateOriginal(value);
    } else {
        return value;
    }
}

export function overridableValueEnsureOriginal<V>(value: OverridableValue<V>): OverridableValueOriginalState<V> {
    if (overridableValueIsOriginal(value)) {
        return value;
    } else {
        return overridableValueCreateOriginal(value);
    }
}

/**
 * Toggles between original and overridden state.
 *
 * When toggling original → overridden, `calculateOverriddenValue` is invoked to
 * produce the new overridden value. It receives the inner value stored in the
 * current original state (which may be `undefined`), so the caller can pick a
 * sensible concrete `V` (e.g. via a default) instead of relying on the inner
 * value being defined.
 */
export function overridableValueToggleOriginal<V>(
    value: OverridableValue<V>,
    isOriginal: Evaluable<(isOriginal: boolean) => boolean>,
    calculateOverriddenValue: Evaluable<(currentInnerValue: V | undefined) => V>
): OverridableValue<V> {
    const wasOriginal = overridableValueIsOriginal(value);

    const evaluatedIsOriginal = evaluateWhenFunction(isOriginal, wasOriginal);

    if (wasOriginal === evaluatedIsOriginal) {
        return value;
    }

    if (wasOriginal) {
        return evaluateWhenFunction(calculateOverriddenValue, value.value);
    } else {
        return overridableValueCreateOriginal(value);
    }
}

export function assertOverridableValue<V>(overridableValue: OverridableValue<V>, assertionLogic: AssertionLogicFunction<V>): asserts overridableValue is OverridableValue<V>;
export function assertOverridableValue<V>(overridableValue: unknown, assertionLogic: AssertionLogicFunction<V>): asserts overridableValue is OverridableValue<V>;
export function assertOverridableValue<V>(overridableValue: unknown, assertionLogic: AssertionLogicFunction<V>): asserts overridableValue is OverridableValue<V> {
    assertType<OverridableValue<V>>(overridableValue, assert => {
        (assert as any).isUnion(
            assertionLogic,
            (assert: NarrowedNonUnionTypeAssertController<OverridableValueOriginalState<V>>) => assert.isObject({
                __original: assert => assert.isOneOfValues([true]),
                value: (assert: any) => assert.isUnion(
                    (innerAssert: any) => innerAssert.isUndefined(),
                    assertionLogic
                )
            })
        )
    })
}
